import { useState } from 'react';
import { breakpoints, Button, Dialog, Inline, Stack, Text } from '@etg/wings';
import { LoadingDots } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { FormProvider, useForm } from 'react-hook-form';
import { constants } from '@eti/utils';
import { sendEmail } from '../../utils/receiptEmailUtil';
import {
  isRequired,
  isValidEmail,
  MAX_CHARS,
} from '../../../../common/components/formComponents/validations';
import FormInput from '../../../../common/components/formComponents/formInput/FormInput';

const messageClass = css`
  text-align: center;

  @media (min-width: ${breakpoints._970}) {
    font-size: 1rem;
    min-height: 63px;
  }
`;

export interface ReceiptMailDialogProps {
  customerEmail?: string;
  isOpen: boolean;
  orderNumber?: string | null;
  onClose: () => void;
}

type ReceiptMailFormType = {
  confirmationMailAddress: string;
};

const ReceiptMailDialog = ({
  customerEmail,
  isOpen,
  orderNumber,
  onClose,
}: ReceiptMailDialogProps) => {
  const { t } = useTranslation();
  const methods = useForm<ReceiptMailFormType>({
    mode: 'onBlur',
    defaultValues: { confirmationMailAddress: customerEmail },
  });

  const { handleSubmit, getValues, reset } = methods;

  const [pendingResponse, setPendingResponse] = useState(false);
  const [success, setSuccess] = useState(false);

  const sendReceiptEmail = async ({ confirmationMailAddress }: ReceiptMailFormType) => {
    if (pendingResponse) {
      return;
    }
    setPendingResponse(true);
    await sendEmail(confirmationMailAddress);
    setPendingResponse(false);
    setSuccess(true);
  };

  const handleDialogClose = () => {
    onClose();
    reset();
    setSuccess(false);
  };

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="confirmation-email-dialog"
      isOpen={isOpen}
      onDismiss={handleDialogClose}
      title={t('Receipt.ReceiptDetails.MailModal.Heading')}
    >
      {success ? (
        <Stack spacing={24}>
          <Text asHtml className={cx(messageClass, constants.MOUSE_FLOW_CLASS_NAMES)}>
            {t(
              'Receipt.ReceiptDetails.MailModal.SuccessMessage',
              orderNumber,
              getValues('confirmationMailAddress'),
            )}
          </Text>
          <Inline align="center">
            <Button
              data-testid="receiptMailModalSuccess-close-button"
              onClick={handleDialogClose}
              variant="primary"
            >
              {t('Receipt.ReceiptDetails.MailModal.CloseLabel')}
            </Button>
          </Inline>
        </Stack>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(sendReceiptEmail)}>
            <Stack>
              <FormInput
                className={constants.MOUSE_FLOW_CLASS_NAMES}
                data-testid="confirmationMailAddress-input"
                isRequired
                label={t('Receipt.ReceiptDetails.MailModal.InputLabel')}
                maxLength={MAX_CHARS}
                name="confirmationMailAddress"
                type="email"
                validations={{
                  ...isRequired(t('Validation.Required')),
                  ...isValidEmail(t('Validation.Mail.Required')),
                }}
              />
              <Inline align="end" collapseBelow={breakpoints._560}>
                <Button
                  data-testid="receiptMailModal-sendEmail-button"
                  type="submit"
                  variant="primary"
                >
                  {pendingResponse ? (
                    <LoadingDots ariaLabel={t('ScreenReader.SendConfirmation.Loading.AriaLabel')} />
                  ) : (
                    t('Receipt.ReceiptDetails.MailModal.SubmitLabel')
                  )}
                </Button>
              </Inline>
            </Stack>
          </form>
        </FormProvider>
      )}
    </Dialog>
  );
};

export default ReceiptMailDialog;
